import axios from 'axios'
import toast from 'react-hot-toast'
import i18next from 'i18next'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// ============================================
// TOKEN REFRESH MUTEX - Prevents race conditions
// ============================================
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// ============================================
// RETRY LOGIC - Prevents premature logout on network issues
// ============================================
let consecutiveAuthFailures = 0;
const MAX_AUTH_FAILURES = 3;
const RETRY_BACKOFF_MS = 1000; // 1 second base backoff

// ============================================
// WAKE-UP DETECTION - Prevents 401 storm after iPad sleep
// When iPad wakes from sleep, all polling intervals fire simultaneously
// with expired access tokens, causing a cascade of 401 errors.
// This mechanism detects wake-up and proactively refreshes the token
// BEFORE polling requests go out, queuing them until refresh completes.
// ============================================
let lastHiddenAt: number | null = null;
let isWakingUp = false;
let wakeUpSubscribers: ((token: string) => void)[] = [];
const WAKE_UP_THRESHOLD_MS = 5000; // 5 seconds hidden = trigger proactive refresh

const subscribeToWakeUp = (callback: (token: string) => void) => {
  wakeUpSubscribers.push(callback);
};

const onWakeUpComplete = (token: string) => {
  wakeUpSubscribers.forEach(cb => cb(token));
  wakeUpSubscribers = [];
};

const subscribeToTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

// Global loading state management
let loadingRequests = 0
let setGlobalLoading: ((loading: boolean, message?: string) => void) | null = null

export const setLoadingHandler = (handler: (loading: boolean, message?: string) => void) => {
  setGlobalLoading = handler
}

const updateGlobalLoading = (increment: boolean, message?: string) => {
  if (increment) {
    loadingRequests++
    if (loadingRequests === 1 && setGlobalLoading) {
      setGlobalLoading(true, message)
    }
  } else {
    loadingRequests = Math.max(0, loadingRequests - 1)
    if (loadingRequests === 0 && setGlobalLoading) {
      setGlobalLoading(false)
    }
  }
}

/**
 * LOT-GATING-UX (G-3) — reconnaît un refus du guard d'essai gratuit backend.
 *
 * Les 403 de GatingGuard portent un `code` stable préfixé TRIAL_
 * (TRIAL_QUOTA_EXCEEDED, TRIAL_SMS_BLOCKED — gating.constants.ts backend).
 * Dupliqué depuis services/gating.ts À DESSEIN : gating.ts importe ./api,
 * l'inverse créerait un cycle de modules dans LE fichier le plus fragile du
 * frontend. Exporté pour être testé tel que l'intercepteur le consomme.
 */
export function isTrialGatingError(error: unknown): boolean {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code
  return typeof code === 'string' && code.startsWith('TRIAL_')
}

/**
 * PAUSE-PLATEAU (recette D3) — reconnaît le 403 « plateau en pause » du
 * serveur (`telephony/tenant-pause.ts` backend). L'intercepteur ne le toaste
 * PAS : la scène affiche déjà son bandeau clair, et un toast rouge pour une
 * décision du client était le doublon vu en recette. Même mécanique que G-3.
 */
export function isTenantPausedError(error: unknown): boolean {
  const response = (error as { response?: { status?: unknown; data?: { code?: unknown } } })
    ?.response
  return response?.status === 403 && response?.data?.code === 'TENANT_PAUSED'
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================
// WAKE-UP HANDLER - Proactive token refresh on iPad resume
// ============================================
const handleWakeUpRefresh = async () => {
  if (isRefreshing || isWakingUp) return; // Already refreshing
  
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) return;

  isWakingUp = true;
  isRefreshing = true;

  try {
    console.log('🔄 Wake-up detected: proactive token refresh...');
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken: refreshTokenValue,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('accessToken', accessToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }

    console.log('✅ Wake-up refresh successful');
    consecutiveAuthFailures = 0;
    isRefreshing = false;
    isWakingUp = false;
    onTokenRefreshed(accessToken);
    onWakeUpComplete(accessToken);
  } catch (error) {
    console.warn('⚠️ Wake-up refresh failed (token may still be valid):', error);
    isRefreshing = false;
    isWakingUp = false;
    // Release queued requests with current token - they'll get 401 and handle it normally
    const currentToken = localStorage.getItem('accessToken') || '';
    onWakeUpComplete(currentToken);
  }
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt = Date.now();
    } else if (document.visibilityState === 'visible' && lastHiddenAt) {
      const hiddenDuration = Date.now() - lastHiddenAt;
      lastHiddenAt = null;

      if (hiddenDuration > WAKE_UP_THRESHOLD_MS) {
        console.log(`📱 iPad wake-up detected (hidden ${Math.round(hiddenDuration / 1000)}s) - refreshing token...`);
        handleWakeUpRefresh();
      }
    }
  });
}

// Request interceptor to add auth token and start loading
api.interceptors.request.use(
  (config) => {
    // If waking up from sleep, queue request until proactive refresh completes
    if (isWakingUp) {
      return new Promise<typeof config>((resolve) => {
        subscribeToWakeUp((newToken: string) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(config);
        });
      });
    }

    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add ngrok header for development
    if (API_BASE_URL.includes('ngrok')) {
      config.headers['ngrok-skip-browser-warning'] = 'true'
    }
    
    // Start loading for non-background requests
    if (!config.headers['X-Background-Request']) {
      const loadingMessage = config.headers['X-Loading-Message'] as string || 'Loading...'
      updateGlobalLoading(true, loadingMessage)
    }
    
    return config
  },
  (error) => {
    updateGlobalLoading(false)
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh and stop loading
api.interceptors.response.use(
  (response) => {
    // Stop loading for non-background requests
    if (!response.config.headers['X-Background-Request']) {
      updateGlobalLoading(false)
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Stop loading for failed request
    if (!originalRequest.headers['X-Background-Request']) {
      updateGlobalLoading(false)
    }

    // Don't intercept 401 errors from login/register endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                          originalRequest.url?.includes('/auth/register')
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      // ✅ FIX: Use mutex to prevent concurrent refresh attempts
      // If already refreshing, queue this request to wait for the new token
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeToTokenRefresh((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
          // If refresh fails while waiting, reject this request too
          // This is handled by onRefreshFailed clearing subscribers
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        // Show info toast that we're refreshing the session (only once)
        toast.loading('Refreshing session...', { id: 'token-refresh' })

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data
        localStorage.setItem('accessToken', accessToken)
        // ✅ FIX: Also save new refresh token (rotation)
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken)
        }

        // Dismiss loading toast and show success
        toast.dismiss('token-refresh')
        toast.success('Session refreshed', { duration: 2000 })

        isRefreshing = false;
        // Notify all waiting requests with the new token
        onTokenRefreshed(accessToken);
        
        // ✅ Reset failure counter on success
        consecutiveAuthFailures = 0;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        isRefreshing = false;
        consecutiveAuthFailures++;
        
        console.warn(`🔄 Auth refresh failed (attempt ${consecutiveAuthFailures}/${MAX_AUTH_FAILURES})`);

        // ✅ FIX: Retry with backoff before giving up
        if (consecutiveAuthFailures < MAX_AUTH_FAILURES) {
          toast.dismiss('token-refresh');
          toast.loading(`Reconnecting... (${consecutiveAuthFailures}/${MAX_AUTH_FAILURES})`, { id: 'token-refresh' });
          
          // Wait with exponential backoff
          await new Promise(resolve => setTimeout(resolve, RETRY_BACKOFF_MS * consecutiveAuthFailures));
          
          // Reset retry flag to allow another attempt
          originalRequest._retry = false;
          
          // Retry the original request (will trigger refresh again)
          return api(originalRequest);
        }
        
        // Max retries reached - logout
        onRefreshFailed();
        consecutiveAuthFailures = 0; // Reset for next session

        // Dismiss loading toast and show error
        toast.dismiss('token-refresh')
        toast.error('Session expired. Please log in again.', { 
          duration: 4000,
          id: 'session-expired' 
        })
        
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        
        // Small delay to let user see the toast before redirect
        setTimeout(() => {
          window.location.href = '/login'
        }, 1000)
        
        return Promise.reject(refreshError)
      }
    }

    // Show error toast for non-401 errors (but not for background requests).
    // LOT-GATING-UX (G-3) : les 403 du guard d'essai (code TRIAL_*) ne toastent
    // PAS ici — le composant appelant affiche déjà l'état (Alert de la modale,
    // toast de la scène v2). Deux émetteurs pour le même refus = doublon vu en
    // recette. Prédicat local : gating.ts importe ./api, on n'importe pas en
    // retour (pas de cycle) — le préfixe TRIAL_ est le contrat stable
    // (gating.constants.ts backend).
    if (
      error.response?.status !== 401 &&
      !originalRequest.headers['X-Background-Request'] &&
      !isTrialGatingError(error) &&
      // PAUSE-PLATEAU (D3) : le bandeau de la scène dit déjà la pause.
      !isTenantPausedError(error)
    ) {
      if (error.response?.status === 429) {
        // #194 (D8) : refus du limiteur de débit — message traduit, et UN seul
        // toast (id fixe) même si dix requêtes d'une page sont refusées ensemble.
        // Pas de toast pour le login (D-E) : la page de connexion affiche son
        // propre message, un seul.
        if (!originalRequest.url?.includes('/auth/login')) {
          toast.error(i18next.t('common:errors.rateLimited'), { id: 'rate-limited', duration: 4000 })
        }
      } else {
        const errorMessage = error.response?.data?.message || 'An error occurred'
        toast.error(errorMessage, { duration: 4000 })
      }
    }

    return Promise.reject(error)
  }
)

// Helper functions for custom loading messages
export const apiWithLoading = {
  get: (url: string, message?: string, config?: any) => 
    api.get(url, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Loading-Message': message 
      } 
    }),
  
  post: (url: string, data?: any, message?: string, config?: any) => 
    api.post(url, data, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Loading-Message': message 
      } 
    }),
  
  put: (url: string, data?: any, message?: string, config?: any) => 
    api.put(url, data, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Loading-Message': message 
      } 
    }),
  
  patch: (url: string, data?: any, message?: string, config?: any) => 
    api.patch(url, data, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Loading-Message': message 
      } 
    }),
  
  delete: (url: string, message?: string, config?: any) => 
    api.delete(url, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Loading-Message': message 
      } 
    }),
}

// Background API calls (no loading indicator)
export const apiBackground = {
  get: (url: string, config?: any) => 
    api.get(url, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Background-Request': 'true' 
      } 
    }),
  
  post: (url: string, data?: any, config?: any) => 
    api.post(url, data, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Background-Request': 'true' 
      } 
    }),
  
  patch: (url: string, data?: any, config?: any) => 
    api.patch(url, data, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Background-Request': 'true' 
      } 
    }),
  
  delete: (url: string, config?: any) => 
    api.delete(url, { 
      ...config, 
      headers: { 
        ...config?.headers, 
        'X-Background-Request': 'true' 
      } 
    }),
}

export default api
